/*
 * Infinite marquee with scroll direction — Osmo setup.
 * https://www.osmo.supply/resource/marquee-with-scroll-direction
 *
 * Autoplays an infinite horizontal loop; scrolling up flips its direction and scroll velocity
 * boosts its speed (both eased). Requires gsap + ScrollTrigger (loaded in <head>).
 * Motion-allowed users only (gsap.matchMedia): under reduced motion nothing is cloned or animated,
 * and the CSS lays the single set out as a static logo wall. The loop pauses while off-screen.
 *
 * Markup: <wp-marquee> (display: contents) > [data-marquee] > [data-marquee-scroll] >
 * [data-marquee-collection] > items. Built in connectedCallback and fully reverted (tweens, trigger,
 * clones) in disconnectedCallback, so a theme-editor re-render starts clean.
 * Config (on [data-marquee]): data-marquee-speed, -direction (left|right), -duplicate, -scroll-speed.
 */
(function () {
  if (!window.gsap || !window.ScrollTrigger || customElements.get('wp-marquee')) return;

  const build = (marquee) => {
    const scroll = marquee.querySelector('[data-marquee-scroll]');
    const collection = marquee.querySelector('[data-marquee-collection]');
    if (!scroll || !collection || !collection.children.length) return;

    const speed = parseFloat(marquee.dataset.marqueeSpeed) || 20;
    // The base tween moves LEFT (xPercent -100 played forward), so "left" = +1, "right" = reverse.
    const dir = marquee.dataset.marqueeDirection === 'right' ? -1 : 1;
    const duplicate = parseInt(marquee.dataset.marqueeDuplicate, 10) || 2;
    const scrollSpeed = parseFloat(marquee.dataset.marqueeScrollSpeed) || 4;

    // Duplicate the collection so the loop is seamless and always fills the viewport. Clones are
    // decorative repeats, so hide them from assistive tech and keep their links out of the tab order.
    const clones = [];
    for (let i = 0; i < duplicate; i++) {
      const clone = collection.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      scroll.appendChild(clone);
      clones.push(clone);
    }
    const collections = scroll.querySelectorAll('[data-marquee-collection]');

    // Duration proportional to one collection's width so speed reads consistently across viewports.
    const duration = Math.max((collection.offsetWidth / window.innerWidth) * speed, 1);

    const loop = gsap.to(collections, {
      xPercent: -100,
      repeat: -1,
      ease: 'none',
      duration,
      // Keeps an infinite loop seamless when played in reverse (scroll-up / "right" direction).
      onReverseComplete() {
        loop.totalTime(loop.rawTime() + loop.duration() * 100);
      },
    });
    loop.timeScale(dir);

    // Smoothed timeScale so direction flips / velocity boosts ease in and out.
    const proxy = { ts: dir };
    const applyTs = () => loop.timeScale(proxy.ts);

    const trigger = ScrollTrigger.create({
      trigger: marquee,
      start: 'top bottom',
      end: 'bottom top',
      // Only spend frames on the loop while the marquee is on screen.
      onToggle: (self) => (self.isActive ? loop.play() : loop.pause()),
      onUpdate: (self) => {
        const inverted = self.direction === -1; // scrolling up
        marquee.setAttribute('data-marquee-status', inverted ? 'inverted' : 'normal');
        const boost = 1 + Math.min(Math.abs(self.getVelocity()) / 500, scrollSpeed);
        gsap.to(proxy, {
          ts: (inverted ? -dir : dir) * boost,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true,
          onUpdate: applyTs,
        });
      },
    });
    if (!trigger.isActive) loop.pause();

    // Ease back to the base autoplay speed/direction when scrolling stops.
    const onScrollEnd = () => {
      marquee.setAttribute('data-marquee-status', 'normal');
      gsap.to(proxy, {
        ts: dir,
        duration: 0.6,
        ease: 'power2.out',
        overwrite: true,
        onUpdate: applyTs,
      });
    };
    ScrollTrigger.addEventListener('scrollEnd', onScrollEnd);

    // matchMedia reverts the loop + ScrollTrigger itself; undo what it can't see (the proxy tweens
    // are created later, in callbacks, so they're outside its context).
    // Reverting the endless loop renders it back to time 0, which fires onReverseComplete and jumps it
    // to the end again — so it's left at xPercent -100. Clear that here (this runs after the revert).
    return () => {
      ScrollTrigger.removeEventListener('scrollEnd', onScrollEnd);
      gsap.killTweensOf(proxy);
      clones.forEach((clone) => clone.remove());
      gsap.set(collection, { clearProps: 'transform' });
      marquee.setAttribute('data-marquee-status', 'normal');
    };
  };

  class WpMarquee extends HTMLElement {
    connectedCallback() {
      const marquee = this.querySelector('[data-marquee]');
      if (!marquee) return;

      // The duration is measured from the logos' laid-out width, so on first page load wait for
      // images; a theme-editor re-render (document already complete) builds straight away.
      this.init = () => {
        this.init = null;
        this.mm = gsap.matchMedia();
        this.mm.add('(prefers-reduced-motion: no-preference)', () => build(marquee));
      };
      if (document.readyState === 'complete') this.init();
      else window.addEventListener('load', this.init, { once: true });
    }

    disconnectedCallback() {
      if (this.init) window.removeEventListener('load', this.init);
      this.init = null;
      if (this.mm) this.mm.revert();
      this.mm = null;
    }
  }

  customElements.define('wp-marquee', WpMarquee);
})();
